import React, { useEffect, useRef, useState, useCallback } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
    MapContainer,
    TileLayer,
    GeoJSON,
    LayersControl,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import { Box } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import "leaflet/dist/leaflet.css";
import { FaCircleInfo, FaArrowRotateLeft } from "react-icons/fa6";
import { Thermometer, Waves, Anchor } from "lucide-react";
import { renderToString } from "react-dom/server";
import L from "leaflet";
import "./WeatherStation.css";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || '';

const INITIAL_VIEW_STATE = {
    longitude: 125.7275,
    latitude: -8.8742,
    zoom: 8,
};

// Marker icons — classic map-pin (teardrop) markers, a Lucide glyph per
// station type: Synoptic (incl. AWS) = thermometer, Tide Gauge = waves,
// Buoy = anchor. Each type gets its own muted, professional accent color;
// the selected station gets a red outline so it stays distinguishable.
const STATION_TYPE_CONFIG = {
    SYNOPTIC: { icon: Thermometer, color: "#4d6b8a", label: "Synoptic / AWS" },
    TIDE_GAUGE: { icon: Waves, color: "#2f8a83", label: "Tide Gauge" },
    BUOY: { icon: Anchor, color: "#b5762e", label: "Buoy" },
};
const DEFAULT_STATION_TYPE = "SYNOPTIC";

const getStationTypeKey = (type) => {
    const key = String(type || "").toUpperCase().replace(/\s+/g, "_");
    return STATION_TYPE_CONFIG[key] ? key : DEFAULT_STATION_TYPE;
};

// Teardrop pin path in a 32x42 viewBox: a circular head centered at (16,16)
// tapering to a point at (16,42) — the tip is what anchors to the station's
// lat/lng, matching the standard Google-Maps-style pin.
const PIN_PATH = "M16 0C7.16 0 0 7.16 0 16c0 11.5 16 26 16 26s16-14.5 16-26C32 7.16 24.84 0 16 0z";

const makeStationIcon = (typeKey, selected) => {
    const { icon: IconComponent, color } = STATION_TYPE_CONFIG[typeKey];
    const width = selected ? 24 : 19;
    const height = selected ? 32 : 25;
    const stroke = selected ? "#dc2626" : "#ffffff";
    const strokeWidth = selected ? 2 : 1.4;
    const iconMarkup = renderToString(<IconComponent size={14} color="#ffffff" strokeWidth={2.4} />);
    const html = `
        <svg width="${width}" height="${height}" viewBox="0 0 32 42" style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35));">
            <path d="${PIN_PATH}" fill="${color}" stroke="${stroke}" stroke-width="${strokeWidth}" />
            <circle cx="16" cy="16" r="10.5" fill="rgba(255,255,255,0.12)" />
            <g transform="translate(9,9)">${iconMarkup}</g>
        </svg>
    `;
    return L.divIcon({
        className: "station-pin-marker",
        html,
        iconSize: [width, height],
        iconAnchor: [width / 2, height],
        popupAnchor: [0, -height + 4],
    });
};

const STATION_ICONS = Object.fromEntries(
    Object.keys(STATION_TYPE_CONFIG).map((key) => [key, makeStationIcon(key, false)])
);

const SELECTED_STATION_ICONS = Object.fromEntries(
    Object.keys(STATION_TYPE_CONFIG).map((key) => [key, makeStationIcon(key, true)])
);

// Legend swatches — a small version of the same pin marker used on the map
const LEGEND_SHAPE_ICON = Object.fromEntries(
    Object.entries(STATION_TYPE_CONFIG).map(([key, { icon: IconComponent, color }]) => {
        const iconMarkup = renderToString(<IconComponent size={10} color="#ffffff" strokeWidth={2.4} />);
        return [
            key,
            <svg width="17" height="22" viewBox="0 0 32 42">
                <path d={PIN_PATH} fill={color} stroke="#ffffff" strokeWidth={1.4} />
                <g transform="translate(11,11)" dangerouslySetInnerHTML={{ __html: iconMarkup }} />
            </svg>,
        ];
    })
);

// Stations from the API don't use consistent field names — normalize once.
const getStationLat = (s) => s?.latitude ?? s?.lat ?? null;
const getStationLng = (s) => s?.longitude ?? s?.lng ?? s?.lon ?? null;
const getStationName = (s) => s?.name || s?.station_name || `Station ${s?.id}`;
const getStationType = (s) => s?.type || s?.station_type || "Unknown";

// Component to center map on station
const MapController = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        if (center) {
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);
    return null;
};

// Leaflet already closes an open marker popup when the map itself is clicked;
// this closes it on clicks anywhere outside the map (e.g. the stations table).
const PopupCloser = () => {
    const map = useMap();
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!map.getContainer().contains(e.target)) {
                map.closePopup();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [map]);
    return null;
};

const InfoButton = ({ onClick }) => {
    // Leaflet's own drag/click handling swallows clicks on overlay controls
    // unless propagation to the map is explicitly disabled on the DOM node.
    const btnRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    return (
        <div
            ref={btnRef}
            style={{
                position: "absolute",
                bottom: "20px",
                left: "12px",
                padding: "8px",
                borderRadius: "8px",
                fontSize: "15px",
                cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
                zIndex: 1000,
            }}
            onClick={onClick}
        >
            <FaCircleInfo />
        </div>
    );
};

// Reset button — sits just below the home button (top-left, under the zoom
// controls) and clears all selection/filter state back to its initial values.
const ResetControl = ({ onClick }) => {
    const containerRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    return (
        <div className="leaflet-top leaflet-left" style={{ pointerEvents: "none" }}>
            <div
                ref={containerRef}
                className="leaflet-control leaflet-bar"
                style={{
                    marginTop: "122px",
                    pointerEvents: "auto",
                    background: "#fff",
                    cursor: "pointer",
                }}
                onClick={onClick}
                title="Reset"
            >
                <a
                    href="#reset"
                    role="button"
                    aria-label="Reset"
                    onClick={(e) => e.preventDefault()}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "30px",
                        height: "30px",
                        color: "#333",
                        fontSize: "15px",
                    }}
                >
                    <FaArrowRotateLeft />
                </a>
            </div>
        </div>
    );
};

const WeatherStation = () => {
    const [municipalityData, setMunicipalityData] = useState(null);
    const [subMunicipalityData, setSubMunicipalityData] = useState(null);
    const [sucoData, setSucoData] = useState(null);
    const [info, setInfo] = useState(false);
    const infoRef = useRef(null);
    const infoPanelRef = useCallback((node) => {
        infoRef.current = node;
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);
    useEffect(() => {
        if (!info) return;
        const handleClickOutside = (e) => {
            if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [info]);
    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);
    const [stations, setStations] = useState([]);
    const [loadingStations, setLoadingStations] = useState(true);
    const [stationTypeCounts, setStationTypeCounts] = useState({});
    const [selectedStationData, setSelectedStationData] = useState(null);
    const [loadingStationData, setLoadingStationData] = useState(false);
    const [mapCenter, setMapCenter] = useState([INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]);
    const [selectedStationId, setSelectedStationId] = useState(null);
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d.toISOString().slice(0, 10);
    });
    const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [timeSeriesData, setTimeSeriesData] = useState(null);
    const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
    const [viewMode, setViewMode] = useState("chart");
    const [tablePage, setTablePage] = useState(0);

    // Dynamic RBAC: the download control is shown to Admins and Super Admins
    // (same admin-or-superadmin check used in ActiveEventsHub.js), not the
    // wider RBAC permission set.
    const role = localStorage.getItem("role");
    const isAdmin =
        role === "admin" ||
        role === "superadmin" ||
        localStorage.getItem("is_superuser") === "true";

    const fetchStationData = async (stationId) => {
        try {
            setLoadingStationData(true);
            const response = await fetch(`${backendUrl}cyclone/station-data/${stationId}`);
            const data = await response.json();
            setSelectedStationData(data);
        } catch (error) {
            console.error("Error fetching station data:", error);
            setSelectedStationData(null);
        } finally {
            setLoadingStationData(false);
        }
    };

    const fetchTimeSeries = async (stationId, start, end) => {
        const id = stationId || selectedStationId;
        if (!id) return;
        try {
            setLoadingTimeSeries(true);
            setTimeSeriesData(null);
            setTablePage(0);
            const startTime = `${start || fromDate}T00:00:00Z`;
            const endTime = `${end || toDate}T23:59:59Z`;
            const response = await fetch(
                `${backendUrl}/cyclone/station-data-time-series/${id}/?start_time=${startTime}&end_time=${endTime}`
            );
            const json = await response.json();
            setTimeSeriesData(json.data || json);
        } catch (error) {
            console.error("Error fetching time series:", error);
            setTimeSeriesData(null);
        } finally {
            setLoadingTimeSeries(false);
        }
    };

    const downloadCSV = () => {
        if (!timeSeriesData || Object.keys(timeSeriesData).length === 0) return;

        const params = Object.keys(timeSeriesData).filter(
            (p) => Array.isArray(timeSeriesData[p]) && timeSeriesData[p].length > 0
        );

        // Build merged rows keyed by time
        const rowMap = {};
        params.forEach((param) => {
            timeSeriesData[param].forEach((item) => {
                const t = item.start_time || item.time || item.timestamp || item.datetime;
                if (!t) return;
                if (!rowMap[t]) rowMap[t] = { time: t };
                rowMap[t][param] = item.value;
            });
        });
        const rows = Object.values(rowMap).sort(
            (a, b) => new Date(a.time) - new Date(b.time)
        );

        const unitMap = {
            precipitation: "mm", pressure: "hPa", temperature: "°C",
            gust: "m/s", humidity: "%", wind_speed: "km/h", wind_direction: "°", tide_level: "m",
        };

        const headers = [
            "Time",
            ...params.map((p) => {
                const unit = Object.entries(unitMap).find(([k]) => p.toLowerCase().includes(k))?.[1] || "";
                const label = p.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                return unit ? `${label} (${unit})` : label;
            }),
        ];

        const csvRows = [
            headers.join(","),
            ...rows.map((row) => [
                `"${new Date(row.time).toLocaleString()}"`,
                ...params.map((p) => (row[p] != null ? row[p] : "")),
            ].join(",")),
        ];

        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const stationName = selectedStationData?.station?.name
            ? selectedStationData.station.name.replace(/\s+/g, "_")
            : "weather_station";
        link.href = url;
        link.setAttribute("download", `${stationName}_${fromDate}_to_${toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Selecting a station — from a map marker click or a table row click —
    // highlights it and kicks off the full station-data fetch for the
    // info/summary/chart panels. The map itself stays put.
    const handleSelectStation = (station) => {
        if (!station) return;
        setSelectedStationId(station.id);
        setTimeSeriesData(null);
        setViewMode("chart");
        fetchStationData(station.id);
    };

    // Resets selection, filters and the map view back to their initial state
    const resetAll = () => {
        setSelectedStationId(null);
        setSelectedStationData(null);
        setTimeSeriesData(null);
        setViewMode("chart");
        setTablePage(0);
        const d = new Date();
        d.setDate(d.getDate() - 30);
        setFromDate(d.toISOString().slice(0, 10));
        setToDate(new Date().toISOString().slice(0, 10));
        setMapCenter([INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]);
    };

    // Calculate station type counts
    const calculateStationTypeCounts = (stationsData) => {
        const counts = {};
        stationsData.forEach(station => {
            const type = station.type || station.station_type || "Unknown";
            counts[type] = (counts[type] || 0) + 1;
        });
        return counts;
    };

    useEffect(() => {
        const loadBoundaryData = async () => {
            try {
                const urls = [
                    "/DATA/OSMbased_adm1_boundary.json",
                    "/DATA/OSMbased_adm2_boundary.json",
                    "/DATA/OSMbased_adm3_boundary.json",
                ];
                const responses = await Promise.all(urls.map((url) => fetch(url)));
                const data = await Promise.all(responses.map((res) => res.json()));

                setMunicipalityData(data[0]);
                setSubMunicipalityData(data[1]);
                setSucoData(data[2]);
            } catch (error) {
                console.error("Error loading boundary data:", error);
            }
        };

        const loadStations = async () => {
            try {
                setLoadingStations(true);
                const response = await fetch(`${backendUrl}/cyclone/station-all/`);
                const data = await response.json();
                const stationsList = Array.isArray(data) ? data : [];
                setStations(stationsList);
                const counts = calculateStationTypeCounts(stationsList);
                setStationTypeCounts(counts);
            } catch (error) {
                console.error("Error loading stations:", error);
            } finally {
                setLoadingStations(false);
            }
        };

        loadBoundaryData();
        loadStations();
    }, []);

    return (
        <div className="weather-station-container">
            {/* Station Type Counts Section */}
            {!loadingStations && Object.keys(stationTypeCounts).length > 0 && (
                <Box
                    sx={{
                        p: 2,
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
                        border: "1px solid #e8ecf1",
                        marginBottom: "16px",
                    }}
                >
                    <div style={{ marginBottom: "12px" }}>
                        <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>
                            Station Statistics
                        </span>
                        <span style={{ fontSize: "13px", color: "#666", marginLeft: "8px" }}>
                            (Total: {stations.length})
                        </span>
                    </div>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                        }}
                    >
                        {Object.entries(stationTypeCounts).map(([type, count]) => (
                            <Box
                                key={type}
                                sx={{
                                    flex: "1 1 auto",
                                    minWidth: "150px",
                                    padding: "12px 16px",
                                    backgroundColor: "#f8f9fa",
                                    borderRadius: "8px",
                                    border: "1px solid #e0e0e0",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "#e9ecef",
                                        transform: "translateY(-2px)",
                                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                                    },
                                }}
                            >
                                <span style={{ fontSize: "24px", fontWeight: 700, color: "#0d6efd" }}>
                                    {count}
                                </span>
                                <span style={{ fontSize: "13px", color: "#666", marginTop: "4px", textAlign: "center" }}>
                                    {type}
                                </span>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Loading Overlay */}
            {loadingStationData && (
                <div className="ws-loading-overlay">
                    <div className="ws-spinner" />
                    <span className="ws-loading-text">Loading Station Data...</span>
                </div>
            )}

            {/* Row 1: Map (all stations plotted) on the left, Stations table on the right.
                Click a marker or a table row to select a station and load its info below. */}
            <Box sx={{ display: "flex", gap: 2, marginBottom: "16px", flexWrap: "wrap", alignItems: "stretch" }}>
                {/* Map Container */}
                <Box
                    sx={{
                        flex: "1 1 55%",
                        minWidth: "320px",
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    <MapContainer
                        center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
                        zoom={INITIAL_VIEW_STATE.zoom}
                        style={{ width: "100%", height: "480px" }}
                    >
                      <MapHomeControl />
                        <ResetControl onClick={resetAll} />
                        <MapController center={mapCenter} zoom={INITIAL_VIEW_STATE.zoom} />
                        <PopupCloser />

                        {info && (
                            <div
                                ref={infoPanelRef}
                                style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
                            >
                                Click any station marker (or a row in the table) to view its readings.
                            </div>
                        )}
                        <InfoButton onClick={() => setInfo(!info)} />

                        <MapLegendControl
                            width="150px"
                            sections={[
                                {
                                    title: "Station Type",
                                    items: Object.entries(STATION_TYPE_CONFIG).map(([key, { label, color }]) => ({
                                        label,
                                        color,
                                        icon: LEGEND_SHAPE_ICON[key],
                                    })),
                                },
                            ]}
                        />

                        {/* Every station is plotted; the selected one is highlighted in red */}
                        {stations.map((station) => {
                            const lat = getStationLat(station);
                            const lng = getStationLng(station);
                            if (lat == null || lng == null) return null;
                            const isSelected = station.id === selectedStationId;
                            const typeKey = getStationTypeKey(getStationType(station));
                            return (
                                <Marker
                                    key={station.id}
                                    position={[lat, lng]}
                                    icon={(isSelected ? SELECTED_STATION_ICONS : STATION_ICONS)[typeKey]}
                                    eventHandlers={{ click: () => handleSelectStation(station) }}
                                >
                                    <Popup>
                                        <div style={{ minWidth: "150px" }}>
                                            <div style={{ fontWeight: 700, fontSize: "14px", marginBottom: "8px" }}>
                                                {getStationName(station)}
                                            </div>
                                            <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                                                <strong>Type:</strong> {getStationType(station)}
                                            </div>
                                            <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                                                <strong>Lat:</strong> {lat.toFixed(4)}
                                            </div>
                                            <div style={{ fontSize: "12px", marginBottom: "4px" }}>
                                                <strong>Long:</strong> {lng.toFixed(4)}
                                            </div>
                                            <div style={{ fontSize: "12px" }}>
                                                <strong>Elevation:</strong> {station.elevation != null ? `${station.elevation} m` : "--"}
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })}

                <LayersControl position="topright">
                    <LayersControl.BaseLayer name="Map" checked>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                    </LayersControl.BaseLayer>
                    {municipalityData && (
                        <LayersControl.Overlay name="Municipality" checked>
                            <GeoJSON
                                data={municipalityData}
                                style={{
                                    color: "#000000",
                                    weight: 1,
                                    opacity: 1,
                                    fillOpacity: 0,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                    {subMunicipalityData && (
                        <LayersControl.Overlay name="Sub-Municipality">
                            <GeoJSON
                                data={subMunicipalityData}
                                style={{
                                    color: "#000000",
                                    weight: 1,
                                    opacity: 1,
                                    fillOpacity: 0,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                    {sucoData && (
                        <LayersControl.Overlay name="Suco">
                            <GeoJSON
                                data={sucoData}
                                style={{
                                    color: "#000000",
                                    weight: 1,
                                    opacity: 1,
                                    fillOpacity: 0,
                                }}
                            />
                        </LayersControl.Overlay>
                    )}
                </LayersControl>
            </MapContainer>
                </Box>

                {/* Stations Table — same visual chrome as CommonTable (header/border/cell styling), just no search/sort/pagination */}
                <Box
                    sx={{
                        flex: "1 1 40%",
                        minWidth: "300px",
                        height: "480px",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid #e2e8f0",
                        borderRadius: "10px",
                        background: "white",
                        overflow: "hidden",
                    }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "13px" }}>
                            <thead>
                                <tr style={{ background: "#f1f5f9" }}>
                                    <th style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, position: "sticky", top: 0, zIndex: 2, background: "#f1f5f9" }}>S.No</th>
                                    <th style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, position: "sticky", top: 0, zIndex: 2, background: "#f1f5f9" }}>Station Name</th>
                                    <th style={{ border: "1px solid #e2e8f0", padding: "12px 10px", textAlign: "left", fontWeight: 700, position: "sticky", top: 0, zIndex: 2, background: "#f1f5f9" }}>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingStations && (
                                    <tr><td colSpan={3} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading stations…</td></tr>
                                )}
                                {!loadingStations && stations.length === 0 && (
                                    <tr><td colSpan={3} style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>No stations found.</td></tr>
                                )}
                                {stations.map((station, i) => {
                                    const isSelected = station.id === selectedStationId;
                                    return (
                                        <tr
                                            key={station.id}
                                            onClick={() => handleSelectStation(station)}
                                            style={{
                                                cursor: "pointer",
                                                borderBottom: "1px solid #f1f5f9",
                                                backgroundColor: isSelected ? "#e7f1ff" : undefined,
                                                transition: "background-color 0.2s ease",
                                            }}
                                        >
                                            <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{i + 1}</td>
                                            <td style={{ border: "1px solid #e2e8f0", padding: "10px", fontWeight: isSelected ? 700 : 400 }}>
                                                {getStationName(station)}
                                            </td>
                                            <td style={{ border: "1px solid #e2e8f0", padding: "10px" }}>{getStationType(station)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Box>
                </Box>
            </Box>

            {/* Row 2: Data Summary Section */}
            {!loadingStationData && selectedStationData && (
                <Box
                    sx={{
                        p: 2.5,
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        border: "1px solid #e8ecf1",
                        marginBottom: "16px",
                    }}
                >
                    <div style={{ marginBottom: "16px", borderBottom: "2px solid #0d6efd", paddingBottom: "8px" }}>
                        <span style={{ fontWeight: 700, fontSize: "16px", color: "#333" }}>
                            Data Summary
                        </span>
                    </div>

                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        {/* Precipitation Data */}
                        {selectedStationData.summary?.precipitation && (
                            <Box sx={{ flex: "1 1 300px" }}>
                                <div style={{ fontWeight: 600, fontSize: "14px", color: "#0d6efd", marginBottom: "8px" }}>
                                    Precipitation
                                </div>
                                <div style={{ display: "grid", gap: "8px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                                        <span style={{ fontSize: "12px", color: "#666" }}>Today:</span>
                                        <span style={{ fontSize: "12px", color: "#333", fontWeight: 600 }}>
                                            {selectedStationData.summary.precipitation.today_mm != null ? `${selectedStationData.summary.precipitation.today_mm} mm` : "--"}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", backgroundColor: "#ffffff", borderRadius: "6px" }}>
                                        <span style={{ fontSize: "12px", color: "#666" }}>Yesterday:</span>
                                        <span style={{ fontSize: "12px", color: "#333", fontWeight: 600 }}>
                                            {selectedStationData.summary.precipitation.yesterday_mm != null ? `${selectedStationData.summary.precipitation.yesterday_mm} mm` : "--"}
                                        </span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", backgroundColor: "#f8f9fa", borderRadius: "6px" }}>
                                        <span style={{ fontSize: "12px", color: "#666" }}>Month to Date:</span>
                                        <span style={{ fontSize: "12px", color: "#333", fontWeight: 600 }}>
                                            {selectedStationData.summary.precipitation.month_to_date_mm != null ? `${selectedStationData.summary.precipitation.month_to_date_mm} mm` : "--"}
                                        </span>
                                    </div>
                                </div>
                            </Box>
                        )}

                        {/* Daily Summary */}
                        {selectedStationData.summary?.daily && (
                            <Box sx={{ flex: "2 1 500px" }}>
                                <div style={{ fontWeight: 600, fontSize: "14px", color: "#0d6efd", marginBottom: "8px" }}>
                                    Daily Summary
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" }}>
                                    {Object.entries(selectedStationData.summary.daily).map(([param, data]) => (
                                        <div key={param} style={{ padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "6px", border: "1px solid #e0e0e0" }}>
                                            <div style={{ fontSize: "12px", fontWeight: 600, color: "#333", marginBottom: "6px", textTransform: "capitalize" }}>
                                                {param.replace(/_/g, " ")}
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "4px" }}>
                                                <span style={{ color: "#666" }}>Max:</span>
                                                <span style={{ color: "#dc3545", fontWeight: 600 }}>
                                                    {data.max?.value != null ? `${data.max.value.toFixed(2)} ${param.includes("temperature") ? "°C" : param.includes("pressure") ? "hPa" : param.includes("humidity") ? "%" : param.includes("speed") ? "m/s" : param.includes("precipitation") ? "mm" : param.includes("wind_direction") ? "°": param.includes("Tide_level") ? "m" : ""}` : "--"}
                                                </span>
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px" }}>
                                                <span style={{ color: "#666" }}>Min:</span>
                                                <span style={{ color: "#28a745", fontWeight: 600 }}>
                                                    {data.min?.value != null ? `${data.min.value.toFixed(2)} ${param.includes("temperature") ? "°C" : param.includes("pressure") ? "hPa" : param.includes("humidity") ? "%" : param.includes("speed") ? "m/s" : param.includes("precipitation") ? "mm" : param.includes("wind_direction") ? "°" : param.includes("Tide_level") ? "m" : ""}` : "--"}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            {/* View Data Section */}
            {!loadingStationData && selectedStationData && (
                <Box
                    sx={{
                        p: 2.5,
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
                        border: "1px solid #e8ecf1",
                        marginBottom: "16px",
                    }}
                >
                    <div style={{ marginBottom: "16px", borderBottom: "2px solid #0d6efd", paddingBottom: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 700, fontSize: "16px", color: "#333" }}>
                            View Data
                        </span>
                        {timeSeriesData && Object.keys(timeSeriesData).length > 0 && (
                            <div style={{ display: "flex", border: "1px solid #ced4da", borderRadius: "6px", overflow: "hidden" }}>
                                <button
                                    onClick={() => setViewMode("chart")}
                                    style={{
                                        display: "flex", alignItems: "center", gap: "5px",
                                        padding: "5px 14px", border: "none", fontSize: "13px", fontWeight: 600,
                                        cursor: "pointer", transition: "all 0.2s ease",
                                        backgroundColor: viewMode === "chart" ? "#0d6efd" : "#fff",
                                        color: viewMode === "chart" ? "#fff" : "#555",
                                    }}
                                >
                                    <BarChartIcon sx={{ fontSize: "16px" }} /> Chart
                                </button>
                                <button
                                    onClick={() => setViewMode("table")}
                                    style={{
                                        display: "flex", alignItems: "center", gap: "5px",
                                        padding: "5px 14px", border: "none", borderLeft: "1px solid #ced4da", fontSize: "13px", fontWeight: 600,
                                        cursor: "pointer", transition: "all 0.2s ease",
                                        backgroundColor: viewMode === "table" ? "#0d6efd" : "#fff",
                                        color: viewMode === "table" ? "#fff" : "#555",
                                    }}
                                >
                                    <TableChartIcon sx={{ fontSize: "16px" }} /> Table
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Date Range Picker */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2, marginBottom: "20px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <label style={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>From:</label>
                            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                                <CalendarTodayIcon
                                    onClick={() => fromDateRef.current?.showPicker()}
                                    sx={{ position: "absolute", left: "8px", fontSize: "15px", color: "#888", cursor: "pointer", zIndex: 1 }}
                                />
                                <input
                                    ref={fromDateRef}
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    style={{
                                        fontSize: "13px",
                                        padding: "6px 10px 6px 30px",
                                        borderRadius: "6px",
                                        border: "1px solid #ced4da",
                                        outline: "none",
                                        color: "#333",
                                    }}
                                />
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <label style={{ fontSize: "13px", fontWeight: 600, color: "#555" }}>To:</label>
                            <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                                <CalendarTodayIcon
                                    onClick={() => toDateRef.current?.showPicker()}
                                    sx={{ position: "absolute", left: "8px", fontSize: "15px", color: "#888", cursor: "pointer", zIndex: 1 }}
                                />
                                <input
                                    ref={toDateRef}
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    style={{
                                        fontSize: "13px",
                                        padding: "6px 10px 6px 30px",
                                        borderRadius: "6px",
                                        border: "1px solid #ced4da",
                                        outline: "none",
                                        color: "#333",
                                    }}
                                />
                            </div>
                        </div>
                        <button
                            onClick={() => fetchTimeSeries()}
                            disabled={loadingTimeSeries}
                            style={{
                                padding: "7px 20px",
                                backgroundColor: loadingTimeSeries ? "#90caf9" : "#0d6efd",
                                color: "#ffffff",
                                border: "none",
                                borderRadius: "8px",
                                fontSize: "13px",
                                fontWeight: 600,
                                cursor: loadingTimeSeries ? "not-allowed" : "pointer",
                                boxShadow: "0 2px 6px rgba(13, 110, 253, 0.3)",
                                transition: "all 0.2s ease",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {loadingTimeSeries ? "Loading..." : "Fetch Data"}
                        </button>
                        {isAdmin && timeSeriesData && Object.keys(timeSeriesData).length > 0 &&
                            Object.values(timeSeriesData).some(v => Array.isArray(v) && v.length > 0) && (
                            <button
                                onClick={downloadCSV}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    padding: "7px 20px",
                                    backgroundColor: "#198754",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "8px",
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    boxShadow: "0 2px 6px rgba(25, 135, 84, 0.3)",
                                    transition: "all 0.2s ease",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                ⬇ Download Data
                            </button>
                        )}
                    </Box>

                    {/* Loading Indicator */}
                    {loadingTimeSeries && (
                        <div style={{ textAlign: "center", padding: "32px", color: "#666", fontSize: "14px" }}>
                            Loading time series data...
                        </div>
                    )}

                    {/* No Data Message */}
                    {!loadingTimeSeries && timeSeriesData !== null && (
                        typeof timeSeriesData !== "object" ||
                        Object.keys(timeSeriesData).length === 0 ||
                        Object.values(timeSeriesData).every(v => !Array.isArray(v) || v.length === 0)
                    ) && (
                        <div style={{ textAlign: "center", padding: "32px", color: "#888", fontSize: "14px" }}>
                            No data available for the selected date range.
                        </div>
                    )}

                    {/* Charts */}
                    {!loadingTimeSeries && timeSeriesData && Object.keys(timeSeriesData).length > 0 && viewMode === "chart" && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {Object.entries(timeSeriesData).map(([param, values]) => {
                                if (!Array.isArray(values) || values.length === 0) return null;

                                const seriesData = values
                                    .filter(item => item != null && (item.start_time || item.time || item.timestamp || item.datetime) && item.value != null)
                                    .map(item => [
                                        new Date(item.start_time || item.time || item.timestamp || item.datetime).getTime(),
                                        typeof item.value === "number" ? item.value : parseFloat(item.value),
                                    ]);

                                if (seriesData.length === 0) return null;

                                const unitMap = {
                                    precipitation: "mm",
                                    pressure: "hPa",
                                    temperature: "°C",
                                    gust: "m/s",
                                    humidity: "%",
                                    wind_speed: "km/h",
                                    wind_direction: "°",
                                    Tide_level: "m",
                                };
                                const unit = Object.entries(unitMap).find(([key]) => param.toLowerCase().includes(key.toLowerCase()))?.[1] || "";

                                const label = param.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                                const yAxisLabel = unit ? `${label} (${unit})` : label;

                                const options = {
                                    chart: { type: "line", height: 260, animation: false },
                                    title: {
                                        text: unit ? `${label} (${unit})` : label,
                                        style: { fontSize: "14px", fontWeight: "600", color: "#333" },
                                    },
                                    xAxis: {
                                        type: "datetime",
                                        title: { text: "Time", style: { fontSize: "12px" } },
                                        labels: { style: { fontSize: "11px" } },
                                    },
                                    yAxis: {
                                        title: { text: yAxisLabel, style: { fontSize: "12px" } },
                                        labels: {
                                            style: { fontSize: "11px" },
                                            format: unit ? `{value} ${unit}` : "{value}",
                                        },
                                    },
                                    series: [{
                                        name: label,
                                        data: seriesData,
                                        color: "#0d6efd",
                                        lineWidth: 1.5,
                                        marker: { radius: 2 },
                                    }],
                                    tooltip: {
                                        xDateFormat: "%Y-%m-%d %H:%M",
                                        valueDecimals: 2,
                                        valueSuffix: unit ? ` ${unit}` : "",
                                    },
                                    legend: { enabled: false },
                                    credits: { enabled: false },
                                };

                                return (
                                    <Box
                                        key={param}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            p: 1.5,
                                            backgroundColor: "#fafafa",
                                        }}
                                    >
                                        <HighchartsReact highcharts={Highcharts} options={options} />
                                    </Box>
                                );
                            })}
                        </Box>
                    )}

                    {/* Table */}
                    {!loadingTimeSeries && timeSeriesData && Object.keys(timeSeriesData).length > 0 && viewMode === "table" && (() => {
                        const unitMap = {
                            precipitation: "mm", pressure: "hPa", temperature: "°C",
                            gust: "m/s", humidity: "%", wind_speed: "km/h", wind_direction: "°", tide_level: "m"
                        };
                        const params = Object.keys(timeSeriesData).filter(p => Array.isArray(timeSeriesData[p]) && timeSeriesData[p].length > 0);
                        // Build merged rows keyed by start_time
                        const rowMap = {};
                        params.forEach(param => {
                            timeSeriesData[param].forEach(item => {
                                const t = item.start_time || item.time || item.timestamp || item.datetime;
                                if (!t) return;
                                if (!rowMap[t]) rowMap[t] = { time: t };
                                rowMap[t][param] = item.value;
                            });
                        });
                        const rows = Object.values(rowMap).sort((a, b) => new Date(a.time) - new Date(b.time));
                        const PAGE_SIZE = 30;
                        const totalPages = Math.ceil(rows.length / PAGE_SIZE);
                        const pageRows = rows.slice(tablePage * PAGE_SIZE, (tablePage + 1) * PAGE_SIZE);
                        return (
                            <Box>
                                <Box sx={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                        <tr style={{ backgroundColor: "#0d6efd", color: "#fff" }}>
                                            <th style={{ padding: "8px 12px", textAlign: "left", whiteSpace: "nowrap", fontWeight: 600 }}>Time</th>
                                            {params.map(param => {
                                                const unit = Object.entries(unitMap).find(([k]) => param.toLowerCase().includes(k))?.[1] || "";
                                                const label = param.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                                                return (
                                                    <th key={param} style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                                                        {label}{unit ? ` (${unit})` : ""}
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageRows.map((row, i) => (
                                            <tr key={row.time} style={{ backgroundColor: i % 2 === 0 ? "#f8f9fa" : "#fff" }}>
                                                <td style={{ padding: "7px 12px", whiteSpace: "nowrap", color: "#444" }}>
                                                    {new Date(row.time).toLocaleString()}
                                                </td>
                                                {params.map(param => (
                                                    <td key={param} style={{ padding: "7px 12px", textAlign: "right", color: "#333" }}>
                                                        {row[param] != null ? (typeof row[param] === "number" ? row[param].toFixed(2) : row[param]) : "--"}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </Box>
                                {/* Pagination */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                                    <span style={{ fontSize: "13px", color: "#666" }}>
                                        Showing {tablePage * PAGE_SIZE + 1}–{Math.min((tablePage + 1) * PAGE_SIZE, rows.length)} of {rows.length} records
                                    </span>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <button
                                            onClick={() => setTablePage(p => p - 1)}
                                            disabled={tablePage === 0}
                                            style={{
                                                padding: "5px 14px", fontSize: "13px", fontWeight: 600,
                                                borderRadius: "6px", border: "1px solid #ced4da",
                                                backgroundColor: tablePage === 0 ? "#f0f0f0" : "#fff",
                                                color: tablePage === 0 ? "#aaa" : "#333",
                                                cursor: tablePage === 0 ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            ← Prev
                                        </button>
                                        <span style={{ fontSize: "13px", color: "#444", fontWeight: 600 }}>
                                            Page {tablePage + 1} / {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setTablePage(p => p + 1)}
                                            disabled={tablePage >= totalPages - 1}
                                            style={{
                                                padding: "5px 14px", fontSize: "13px", fontWeight: 600,
                                                borderRadius: "6px", border: "1px solid #ced4da",
                                                backgroundColor: tablePage >= totalPages - 1 ? "#f0f0f0" : "#fff",
                                                color: tablePage >= totalPages - 1 ? "#aaa" : "#333",
                                                cursor: tablePage >= totalPages - 1 ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            Next →
                                        </button>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })()}
                </Box>
            )}
        </div>
    );
};

export default WeatherStation;
